// frontend/src/contexts/AuthContext.jsx - FINAL VERSION WITH GOOGLE LOGIN
import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../utils/constants';
import i18n from '../i18n/index';


// NOTE: Since the build environment doesn't fully support import.meta.env,
// we assume VITE_API_URL is available globally or fallback to a hardcoded value
// if running in a non-Vite/browser environment.


const token = localStorage.getItem('gigship_token');

const initialState = {
    user: null,
    token: token,
    isAuthenticated: !!token,
    isLoading: !!token,
    error: null,
    loginAttempts: 0,
    lockoutTime: null
};

const ActionTypes = {
    LOGIN_START: 'LOGIN_START',
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGIN_FAILURE: 'LOGIN_FAILURE',
    LOGIN_REQUIRES_VERIFICATION: 'LOGIN_REQUIRES_VERIFICATION',
    LOGOUT: 'LOGOUT',
    REGISTER_START: 'REGISTER_START',
    REGISTER_SUCCESS: 'REGISTER_SUCCESS',
    REGISTER_FAILURE: 'REGISTER_FAILURE',
    LOAD_USER_START: 'LOAD_USER_START',
    LOAD_USER_SUCCESS: 'LOAD_USER_SUCCESS',
    LOAD_USER_FAILURE: 'LOAD_USER_FAILURE',
    UPDATE_PROFILE: 'UPDATE_PROFILE',
    VERIFY_PHONE_START: 'VERIFY_PHONE_START',
    VERIFY_PHONE_SUCCESS: 'VERIFY_PHONE_SUCCESS',
    VERIFY_PHONE_FAILURE: 'VERIFY_PHONE_FAILURE',
    SWITCH_ROLE: 'SWITCH_ROLE',
    UPDATE_RANKING: 'UPDATE_RANKING',
    CLEAR_ERROR: 'CLEAR_ERROR',
    SET_LOCKOUT: 'SET_LOCKOUT',
    CLEAR_LOCKOUT: 'CLEAR_LOCKOUT'
};

const authReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.LOGIN_START:
        case ActionTypes.REGISTER_START:
        case ActionTypes.LOAD_USER_START:
        case ActionTypes.VERIFY_PHONE_START:
            return { ...state, isLoading: true, error: null };

        case ActionTypes.LOGIN_REQUIRES_VERIFICATION:
            return { ...state, isLoading: false };

        case ActionTypes.LOGIN_SUCCESS:
            localStorage.setItem('gigship_token', action.payload.token);
            localStorage.setItem('gigship_user', JSON.stringify(action.payload.user));
            return {
                ...state,
                isAuthenticated: true,
                isLoading: false,
                user: action.payload.user,
                token: action.payload.token,
                error: null,
                loginAttempts: 0,
                lockoutTime: null
            };

        case ActionTypes.REGISTER_SUCCESS:
            if (action.payload.token) {
                localStorage.setItem('gigship_token', action.payload.token);
                localStorage.setItem('gigship_user', JSON.stringify(action.payload.user));
            }
            return {
                ...state,
                isLoading: false,
                error: null,
                user: action.payload.user,
                token: action.payload.token || state.token,
                isAuthenticated: !!action.payload.token
            };

        case ActionTypes.LOAD_USER_SUCCESS:
            localStorage.setItem('gigship_user', JSON.stringify(action.payload.user));
            return {
                ...state,
                isAuthenticated: true,
                isLoading: false,
                user: action.payload.user,
                error: null
            };

        case ActionTypes.VERIFY_PHONE_SUCCESS: {
            const verifiedUser = action.payload.user;
            localStorage.setItem('gigship_user', JSON.stringify(verifiedUser));
            return {
                ...state,
                isLoading: false,
                user: verifiedUser,
                error: null
            };
        }

        case ActionTypes.LOGIN_FAILURE: {
            const newAttempts = state.loginAttempts + 1;
            const shouldLockout = newAttempts >= 5;
            let lockoutTime = state.lockoutTime;
            if (shouldLockout) {
                lockoutTime = Date.now() + 30 * 60 * 1000;
                localStorage.setItem('gigship_lockout', lockoutTime.toString());
            }
            return {
                ...state,
                isAuthenticated: false,
                isLoading: false,
                user: null,
                error: action.payload.message,
                loginAttempts: newAttempts,
                lockoutTime: shouldLockout ? lockoutTime : state.lockoutTime
            };
        }

        case ActionTypes.REGISTER_FAILURE:
        case ActionTypes.LOAD_USER_FAILURE:
        case ActionTypes.VERIFY_PHONE_FAILURE:
            return {
                ...state,
                isLoading: false,
                error: action.payload.message
            };

        case ActionTypes.LOGOUT:
            localStorage.removeItem('gigship_token');
            localStorage.removeItem('gigship_user');
            return {
                ...state,
                user: null,
                token: null,
                isAuthenticated: false,
                isLoading: false,
                error: null
            };

        case ActionTypes.UPDATE_PROFILE: {
            const updatedUserProfile = {
                ...state.user,
                ...action.payload,
            };
            localStorage.setItem('gigship_user', JSON.stringify(updatedUserProfile));
            return { ...state, user: updatedUserProfile };
        }

        case ActionTypes.SWITCH_ROLE: {
            const userWithNewRole = { ...state.user, activeRole: action.payload.role };
            localStorage.setItem('gigship_user', JSON.stringify(userWithNewRole));
            return { ...state, user: userWithNewRole };
        }

        case ActionTypes.UPDATE_RANKING: {
            const userWithNewRanking = {
                ...state.user,
                ranking: { ...state.user.ranking, ...action.payload }
            };
            localStorage.setItem('gigship_user', JSON.stringify(userWithNewRanking));
            return { ...state, user: userWithNewRanking };
        }

        case ActionTypes.CLEAR_ERROR:
            return { ...state, error: null };

        case ActionTypes.SET_LOCKOUT:
            localStorage.setItem('gigship_lockout', action.payload.toString());
            return { ...state, lockoutTime: action.payload };

        case ActionTypes.CLEAR_LOCKOUT:
            localStorage.removeItem('gigship_lockout');
            return { ...state, loginAttempts: 0, lockoutTime: null };

        default:
            return state;
    }
};

const AuthContext = createContext();

// ✅ FIX 1: Normalize base URL — strip any trailing slash, then ensure it ends
// with exactly one /api. This prevents double-pathing bugs like /api/api/ when
// API_BASE_URL already contains /api (e.g. "http://localhost:5000/api").
const normalizedBase = API_BASE_URL.replace(/\/+$/, '');
console.log('Initializing Axios with:', API_BASE_URL);
axios.defaults.baseURL = normalizedBase.endsWith('/api') ? normalizedBase : `${normalizedBase}/api`;

// Initialize Authorization header at module level immediately 
if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState, (init) => {
        const currentToken = localStorage.getItem('gigship_token');
        return {
            ...init,
            token: currentToken,
            isAuthenticated: !!currentToken,
            isLoading: !!currentToken
        };
    });

    // ✅ FIX 3: Track whether the initial loadUser call is still in flight.
    // The 401 interceptor must NOT fire a LOGOUT while we are validating the
    // token for the first time — that would cause an infinite loading loop.
    const isInitialLoadRef = useRef(true);

    useEffect(() => {
        const token = localStorage.getItem('gigship_token');
        if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const respInterceptor = axios.interceptors.response.use(
            (res) => res,
            (error) => {
                // Only auto-logout on 401 when the user is already authenticated
                // AND we are not in the middle of the initial loadUser call.
                if (
                    error.response?.status === 401 &&
                    state.isAuthenticated &&
                    !isInitialLoadRef.current
                ) {
                    dispatch({ type: ActionTypes.LOGOUT });
                    toast.error('Session expired. Please login again.');
                }
                return Promise.reject(error);
            }
        );
        return () => axios.interceptors.response.eject(respInterceptor);
    }, [state.isAuthenticated]);

    useEffect(() => {
        const lockoutTime = localStorage.getItem('gigship_lockout');
        if (lockoutTime) {
            const expiry = parseInt(lockoutTime, 10);
            if (Date.now() < expiry) {
                dispatch({ type: ActionTypes.SET_LOCKOUT, payload: expiry });
            } else {
                dispatch({ type: ActionTypes.CLEAR_LOCKOUT });
            }
        }
    }, []);

    // ✅ FIX: Robust initial user loading with timeout.
    // Uses a ref to avoid stale closure: state.isLoading captured at mount
    // time never updates inside setTimeout, so we track completion via ref.
    const loadCompletedRef = useRef(false);

    useEffect(() => {
        let isMounted = true;

        if (state.token) {
            loadCompletedRef.current = false;

            // Safety timeout — 8 seconds
            const timeoutId = setTimeout(() => {
                if (isMounted && !loadCompletedRef.current) {
                    console.warn('[Auth] 8s timeout — loadUser never completed, clearing session');
                    dispatch({ type: ActionTypes.LOGOUT });
                    localStorage.removeItem('gigship_token');
                }
            }, 8000);

            // Fetch user — clear timeout as soon as it resolves (success or failure)
            loadUser().finally(() => {
                loadCompletedRef.current = true;
                if (isMounted) clearTimeout(timeoutId);
            });

            return () => {
                isMounted = false;
                clearTimeout(timeoutId);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isAccountLocked = () => {
        if (!state.lockoutTime) return false;
        if (Date.now() >= state.lockoutTime) {
            dispatch({ type: ActionTypes.CLEAR_LOCKOUT });
            return false;
        }
        return true;
    };

    const getRemainingLockoutTime = () =>
        state.lockoutTime ? Math.ceil(Math.max(0, state.lockoutTime - Date.now()) / 60000) : 0;

    // ✅ DEFINITIVE FIX: loadUser with AbortController timeout.
    // The HTTP request itself is aborted after 7 seconds — no stale closure issues.
    const loadUser = async () => {
        const currentToken = state.token || localStorage.getItem('gigship_token');
        if (!currentToken) return;

        dispatch({ type: ActionTypes.LOAD_USER_START });
        isInitialLoadRef.current = true;

        // AbortController: hard-kill the request after 7 seconds
        const controller = new AbortController();
        const abortTimeout = setTimeout(() => controller.abort(), 7000);

        try {
            axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
            const res = await axios.get('/auth/me', { signal: controller.signal });
            clearTimeout(abortTimeout);

            // ── JSON Guard ──
            const contentType = res.headers['content-type'];
            if (contentType && !contentType.includes('application/json')) {
                throw new Error("Server returned HTML instead of JSON. Check API_BASE_URL.");
            }

            // ── CRITICAL FIX: Backend returns { data: user }, not { user: ... } ──
            // Support both shapes for backwards compatibility
            console.log('[DEBUG] typeof res.data:', typeof res.data);
            const userData = res.data.data || res.data.user || res.data;
            console.log('[DEBUG] userData id:', userData?.id);
            if (!userData || !userData.id) {
                throw new Error("Invalid user data received from /auth/me");
            }

            if (userData.preferredLanguage && i18n.language?.split('-')[0] !== userData.preferredLanguage) {
                i18n.changeLanguage(userData.preferredLanguage);
                localStorage.setItem('gigship_language', userData.preferredLanguage);
            }

            dispatch({ type: ActionTypes.LOAD_USER_SUCCESS, payload: { user: userData } });
        } catch (err) {
            clearTimeout(abortTimeout);

            if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
                console.warn('[Auth] User fetch timed out after 7s — aborting');
                dispatch({
                    type: ActionTypes.LOAD_USER_FAILURE,
                    payload: { message: 'SERVER_UNREACHABLE' }
                });
            } else if (!err.response) {
                // Network error (server offline)
                console.error('[Auth] loadUser network error:', err.message);
                dispatch({
                    type: ActionTypes.LOAD_USER_FAILURE,
                    payload: { message: 'SERVER_UNREACHABLE' }
                });
            } else {
                // Token invalid / expired / 401 / 403 / 404
                console.error('[Auth] loadUser auth error:', err.response?.status, err.message);
                dispatch({ type: ActionTypes.LOGOUT });
                localStorage.removeItem('gigship_token');
                delete axios.defaults.headers.common['Authorization'];
            }
        } finally {
            isInitialLoadRef.current = false;
        }
    };

    const login = async (credentials) => {
        if (isAccountLocked()) {
            const t = getRemainingLockoutTime();
            throw new Error(`Account is locked. Try again in ${t} minutes.`);
        }

        dispatch({ type: ActionTypes.LOGIN_START });

        try {
            console.log('📤 Sending login request...');
            const res = await axios.post('/auth/login', credentials);

            console.log('📥 Login response:', res.status);

            const { success, user, token, requiresEmailVerification, userId, email, reason, message } = res.data;

            // ✅ DEVICE VERIFICATION CASE
            if (requiresEmailVerification || (success === false && userId)) {
                console.log('🔒 Device verification required');

                dispatch({ type: ActionTypes.LOGIN_REQUIRES_VERIFICATION });

                toast(message || 'New device detected. Check your email for verification code.', {
                    icon: '🔒',
                    duration: 6000
                });

                return {
                    success: false,
                    requiresEmailVerification: true,
                    userId,
                    email,
                    reason,
                    message
                };
            }

            // ✅ SUCCESSFUL LOGIN CASE
            if (success && token && user) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                if (user.preferredLanguage && i18n.language?.split('-')[0] !== user.preferredLanguage) {
                    i18n.changeLanguage(user.preferredLanguage);
                    localStorage.setItem('gigship_language', user.preferredLanguage);
                }

                dispatch({
                    type: ActionTypes.LOGIN_SUCCESS,
                    payload: { user, token }
                });

                toast.success(`Welcome back, ${user.username}!`);

                return {
                    success: true,
                    user,
                    token,
                    profileStatus: res.data.profileStatus
                };
            }

            // UNEXPECTED RESPONSE
            throw new Error(message || 'Invalid response from server');

        } catch (err) {
            console.error('❌ Login error:', err.response?.status, err.response?.data?.message || err.message); // never err itself: err.config.data holds the typed credentials

            const response = err.response?.data;
            const message = response?.message || err.message || 'Login failed';

            // Check if error response has verification requirement
            if (response?.requiresEmailVerification) {
                console.log('🔒 Device verification required (from error)');

                dispatch({ type: ActionTypes.LOGIN_REQUIRES_VERIFICATION });

                toast(response.message || 'New device detected. Check your email.', {
                    icon: '🔒',
                    duration: 6000
                });

                return {
                    success: false,
                    requiresEmailVerification: true,
                    userId: response.userId,
                    email: response.email,
                    reason: response.reason,
                    message: response.message
                };
            }

            // REGULAR LOGIN FAILURE
            dispatch({
                type: ActionTypes.LOGIN_FAILURE,
                payload: { message }
            });

            toast.error(message);
            throw new Error(message);
        }
    };

    // =================================================================
    // 🚀 GOOGLE OAUTH LOGIN FUNCTION
    // =================================================================

    const googleLogin = async (token) => {
        dispatch({ type: ActionTypes.LOGIN_START });
        try {
            console.log('📤 Sending Google token to backend for verification...');

            const res = await axios.post('/auth/google', { token });

            console.log('📥 Google login response:', res.status);

            const { success, user, token: jwtToken, message } = res.data;

            if (success && jwtToken && user) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;

                dispatch({
                    type: ActionTypes.LOGIN_SUCCESS,
                    payload: { user, token: jwtToken }
                });

                toast.success(`Welcome back via Google, ${user.username}!`);

                return {
                    success: true,
                    user,
                    token: jwtToken
                };
            }

            // UNEXPECTED RESPONSE
            throw new Error(message || 'Invalid response from server during Google login');

        } catch (err) {
            console.error('❌ Google login error:', err.response?.status, err.response?.data?.message || err.message);

            const message = err.response?.data?.message || err.message || 'Google login failed';

            dispatch({
                type: ActionTypes.LOGIN_FAILURE,
                payload: { message }
            });

            toast.error(message);
            throw new Error(message); // ✅ Removed duplicate dead-code lines that existed previously
        }
    };

    // =================================================================
    // 🛡️ ADMIN LOGIN FUNCTION
    // =================================================================

    const adminLogin = async (credentials) => {
        dispatch({ type: ActionTypes.LOGIN_START });
        try {
            console.log('🛡️ Sending ADMIN login request...');
            const res = await axios.post('/auth/admin/login', credentials);

            console.log('📥 Admin login response:', res.status);

            const { success, user, token, message, requiresEmailVerification } = res.data;

            if (requiresEmailVerification) {
                dispatch({ type: ActionTypes.LOGIN_REQUIRES_VERIFICATION });
                toast(message || 'Verification required.', { icon: '🛡️' });
                return res.data; // Pass full response for handling
            }

            if (success && token && user) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                dispatch({
                    type: ActionTypes.LOGIN_SUCCESS,
                    payload: { user, token }
                });

                toast.success(`Welcome back, Administrator ${user.username}!`);
                return { success: true, user, token };
            }

            throw new Error(message || 'Admin login failed');

        } catch (err) {
            console.error('❌ Admin login error:', err.response?.status, err.response?.data?.message || err.message);
            const message = err.response?.data?.message || err.message || 'Admin login failed';

            dispatch({
                type: ActionTypes.LOGIN_FAILURE,
                payload: { message }
            });

            toast.error(message);
            throw new Error(message);
        }
    };

    // =================================================================
    // 🛡️ ADMIN REGISTER FUNCTION
    // =================================================================
    const adminRegister = async (data) => {
        dispatch({ type: ActionTypes.REGISTER_START });
        try {
            console.log('🛡️ Sending admin registration...');
            const res = await axios.post('/auth/admin/register', data);
            const { success, user, token, message } = res.data;

            if (success && token && user) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                dispatch({
                    type: ActionTypes.REGISTER_SUCCESS,
                    payload: { user, token }
                });
                toast.success(message || 'Account created! Admin promotion required.');
                return { success: true, user, token };
            }

            throw new Error(message || 'Registration failed');
        } catch (err) {
            console.error('❌ Admin register error:', err.response?.status, err.response?.data?.message || err.message);
            const message = err.response?.data?.message || err.message || 'Registration failed';
            dispatch({ type: ActionTypes.REGISTER_FAILURE, payload: { message } });
            toast.error(message);
            throw new Error(message);
        }
    };

    // =================================================================
    // 🛡️ ADMIN FORGOT PASSWORD
    // =================================================================
    const adminForgotPassword = async (email) => {
        try {
            await axios.post('/auth/admin/forgot-password', { email });
            toast.success('If an account with that email exists, a reset link has been sent.');
        } catch (err) {
            const message = err.response?.data?.message || 'Failed to send reset email';
            toast.error(message);
            throw new Error(message);
        }
    };

    // =================================================================

    const register = async (data) => {
        dispatch({ type: ActionTypes.REGISTER_START });
        try {
            const res = await axios.post('/auth/register', data);
            const { user, token } = res.data;
            dispatch({
                type: ActionTypes.REGISTER_SUCCESS,
                payload: { user, token }
            });

            if (token) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }

            toast.success(res.data.message || 'Registration successful! Please verify your phone.');
            return { success: true, user, token };
        } catch (err) {
            const message = err.response?.data?.message || 'Registration failed';
            dispatch({ type: ActionTypes.REGISTER_FAILURE, payload: { message } });
            toast.error(message);
            throw new Error(message);
        }
    };

    const verifyPhone = async (verificationCode) => {
        dispatch({ type: ActionTypes.VERIFY_PHONE_START });

        try {
            const response = await axios.post('/auth/verify-phone', {
                phoneNumber: state.user?.phone || state.user?.phoneNumber,
                code: verificationCode
            });

            const { user, token, message } = response.data;

            if (token) {
                localStorage.setItem('gigship_token', token);
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                dispatch({
                    type: ActionTypes.LOGIN_SUCCESS,
                    payload: { user, token }
                });
            } else {
                dispatch({
                    type: ActionTypes.VERIFY_PHONE_SUCCESS,
                    payload: { user }
                });
            }

            toast.success(message || 'Phone number verified successfully!');
            return { success: true, user, token };
        } catch (error) {
            const message = error.response?.data?.message || 'Phone verification failed';

            dispatch({
                type: ActionTypes.VERIFY_PHONE_FAILURE,
                payload: { message }
            });

            toast.error(message);
            throw new Error(message);
        }
    };

    const resendVerificationCode = async () => {
        try {
            await axios.post('/auth/resend-verification', {
                phoneNumber: state.user?.phone || state.user?.phoneNumber
            });

            toast.success('Verification code sent successfully!');
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to resend verification code';
            toast.error(message);
            throw new Error(message);
        }
    };

    const logout = () => {
        delete axios.defaults.headers.common['Authorization'];
        dispatch({ type: ActionTypes.LOGOUT });
        toast.success('Logged out successfully!');
    };

    // =================================================================
    // EXISTING USER & PROFILE FUNCTIONS
    // =================================================================

    const updateProfile = async (data) => {
        try {
            const currentToken = localStorage.getItem('gigship_token') || state.token;

            if (!currentToken) {
                throw new Error('No authentication token found. Please log in again.');
            }

            const res = await axios.put('/users/profile', data, {
                headers: {
                    Authorization: `Bearer ${currentToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const updatedUser = res.data.data?.user || res.data.user || res.data.data;

            if (!updatedUser) {
                throw new Error('Invalid response from server');
            }

            dispatch({ type: ActionTypes.UPDATE_PROFILE, payload: updatedUser });

            toast.success('Profile updated successfully!');
            return { success: true, user: updatedUser };
        } catch (err) {
            const message =
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.message ||
                'Profile update failed';
            toast.error(message);
            throw new Error(message);
        }
    };

    const forgotPassword = async (email) => {
        try {
            await axios.post('/auth/request-password-reset', { email });
            toast.success('Password reset instructions sent to your email!');
        } catch (err) {
            const message = err.response?.data?.message || 'Failed to send reset email';
            toast.error(message);
            throw new Error(message);
        }
    };

    const resetPassword = async (token, newPassword) => {
        try {
            const res = await axios.post('/auth/reset-password', { token, newPassword });
            toast.success('Password reset successfully!');
            return { success: true, message: res.data.message };
        } catch (err) {
            const message = err.response?.data?.message || 'Password reset failed';
            toast.error(message);
            throw new Error(message);
        }
    };

    const changePassword = async (currentPassword, newPassword) => {
        try {
            const res = await axios.put('/auth/change-password', { currentPassword, newPassword });
            // The change signs out every session, this one included: say so and sign out here too
            // (signed out, the protected pages send the user to /login)
            const message = res.data?.message || 'Password changed. Please sign in again with your new password.';
            toast.success(message);
            logout();
            localStorage.removeItem('token'); // legacy token key; LOGOUT clears gigship_token and gigship_user
            return { success: true, signedOut: true, message };
        } catch (err) {
            const message = err.response?.data?.message || 'Password change failed';
            toast.error(message);
            throw new Error(message);
        }
    };

    const updateRanking = (data) => {
        dispatch({ type: ActionTypes.UPDATE_RANKING, payload: data });
    };

    const clearError = () => {
        dispatch({ type: ActionTypes.CLEAR_ERROR });
    };

    // =================================================================
    // USER ROLE & PERMISSION HELPERS
    // =================================================================

    const isGigger = () =>
        !!(state.user?.roles?.includes('gigger') || state.user?.roles?.includes('hybrid'));
    const isShipper = () =>
        !!(state.user?.roles?.includes('shipper') || state.user?.roles?.includes('hybrid'));
    const isMediator = () => !!state.user?.roles?.includes('mediator');
    const isAdmin = () => !!(state.user?.roles?.includes('admin') || state.user?.roles?.includes('superadmin'));
    const isSuperAdmin = () => !!state.user?.roles?.includes('superadmin');
    const hasRole = (r) => !!(state.user?.roles?.includes(r) || state.user?.roles?.includes('hybrid'));

    const isPhoneVerified = () =>
        state.user?.verification?.phone?.verified || state.user?.phoneVerified || false;
    const isEmailVerified = () =>
        state.user?.verification?.email?.verified || state.user?.emailVerified || false;
    const isIdentityVerified = () =>
        state.user?.verification?.identity?.verified || state.user?.identityVerified || false;
    const getUserLevel = () => state.user?.webLevel ?? state.user?.ranking?.level ?? 1;
    const getCommissionRate = () => state.user?.ranking?.commissionRate || 20;

    const canAccessFeature = (feature) => {
        const plan = state.user?.subscription?.plan || 'free';
        const features = state.user?.subscription?.features || [];
        if (plan === 'enterprise') return true;
        if (plan === 'pro' && ['advanced_analytics', 'priority_support'].includes(feature))
            return true;
        return features.includes(feature);
    };

    // ✅ CRITICAL FIX: Proper updateUser function
    const updateUser = (updatedUserData) => {
        console.log('🔄 Updating user in context');
        dispatch({ type: ActionTypes.UPDATE_PROFILE, payload: updatedUserData });
    };

    const setUser = (user) => {
        dispatch({ type: ActionTypes.UPDATE_PROFILE, payload: user });
    };

    // ✅ B3-5: Alias for loadUser — used by Settings components to force a full re-fetch after save
    const refreshUser = () => loadUser();

    return (
        <AuthContext.Provider
            value={{
                ...state,
                login,
                adminLogin,
                adminRegister,
                adminForgotPassword,
                register,
                googleLogin,
                logout,
                loadUser,
                verifyPhone,
                resendVerificationCode,
                forgotPassword,
                resetPassword,
                changePassword,
                updateProfile,
                updateUser,
                setUser,
                refreshUser,
                updateRanking,
                clearError,
                isGigger,
                isShipper,
                isMediator,
                isAdmin,
                isSuperAdmin,
                hasRole,
                isPhoneVerified,
                isEmailVerified,
                isIdentityVerified,
                getUserLevel,
                getCommissionRate,
                canAccessFeature,
                isAccountLocked,
                getRemainingLockoutTime
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
    return ctx;
};

export const withAuth = (Wrapped, requiredRole = null) => {
    return function Authenticated(props) {
        const { isAuthenticated, isLoading, hasRole } = useAuth();
        if (isLoading)
            return (
                <div className="flex items-center justify-center min-h-screen">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            );
        if (!isAuthenticated) return <Navigate to="/login" replace />;
        if (requiredRole && !hasRole(requiredRole))
            return (
                <div className="text-center py-8">
                    <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
                    <p className="text-gray-600 mt-2">
                        You don't have permission to access this page.
                    </p>
                </div>
            );
        return <Wrapped {...props} />;
    };
};

export default AuthContext;